import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import QuickActionsFAB from "@/components/QuickActionsFAB";
import api from "@/lib/api";
import { APP_VERSION_LABEL } from "@/lib/version";
import {
  SquaresFour,
  MapPin,
  Package,
  Truck,
  FileText,
  QrCode,
  ArrowsClockwise,
  ChartBar,
  Gear,
  SignOut,
  List,
  X,
  Buildings,
  Factory,
  Ruler,
  Database,
  CaretDown,
  CaretRight,
  Users as UsersIcon,
  UserCircle,
  PencilSimple,
  ClipboardText,
  WhatsappLogo,
  CalendarBlank,
  Plug,
  FileXls,
  Warning,
  Camera,
} from "@phosphor-icons/react";

const masterChildren = [
  { to: "/districts", label: "Distrik", icon: MapPin },
  { to: "/products", label: "Produk", icon: Package },
  { to: "/trucks", label: "Truk", icon: Truck },
  { to: "/plants", label: "Plant", icon: Factory },
  { to: "/customers", label: "Pelanggan", icon: UserCircle },
];

const laporanChildren = [
  { to: "/reports/spb", label: "Laporan SPB", icon: FileText },
  { to: "/reports/distrik-monthly", label: "Semen per Distrik", icon: ChartBar },
  { to: "/reports/bukti-muat-harian", label: "Bukti Muat Harian", icon: Camera },
  { to: "/reports/so-usage", label: "Pemakaian SO", icon: ClipboardText },
  { to: "/reports/so-remaining", label: "Sisa SO", icon: ClipboardText },
  { to: "/reports/sync-match", label: "Sync Match", icon: ClipboardText },
];

// Full-admin Settings dropdown — sorted alphabetically by label.
const adminSettingsChildren = [
  { to: "/settings/integrations", label: "Integrasi", icon: Plug },
  { to: "/layout", label: "Layout Editor", icon: Ruler },
  { to: "/settings/signatures", label: "Library TTD", icon: PencilSimple },
  { to: "/settings/whatsapp", label: "Notifikasi WA", icon: WhatsappLogo },
  { to: "/settings", label: "Preferensi", icon: Gear },
  { to: "/users", label: "User", icon: UsersIcon },
];

// Admin Kantor Settings — sorted alphabetically. User + WA hanya untuk full
// admin (bukan admin_kantor) per business rule: kantor tidak boleh manage
// user atau broadcast WA.
const adminKantorSettingsChildren = [
  { to: "/settings", label: "Preferensi", icon: Gear },
];

const spbChildren = [
  { to: "/spb", label: "Daftar SPB", icon: FileText },
  { to: "/sales-orders", label: "Sales Order", icon: ClipboardText },
  { to: "/slot-calendar", label: "Kalender Slot", icon: CalendarBlank },
];

const adminNav = [
  { to: "/", label: "Dashboard", icon: SquaresFour },
  { type: "group", label: "SPB", icon: FileText, children: spbChildren },
  { type: "group", label: "Master Data", icon: Database, children: masterChildren },
  { type: "group", label: "Laporan", icon: ChartBar, children: laporanChildren },
  { type: "group", label: "Setelan", icon: Gear, children: adminSettingsChildren },
  { to: "/sheets", label: "Upload Realisasi", icon: FileXls },
  { to: "/mismatch-corrections", label: "Koreksi Mismatch", icon: Warning, badgeKey: "mismatch_pending" },
  { to: "/audit", label: "Audit Trail", icon: ChartBar },
];

// Admin Kantor: same as admin MINUS Layout Editor + Library TTD (bundled into Setelan dropdown).
const adminKantorNav = [
  { to: "/", label: "Dashboard", icon: SquaresFour },
  { type: "group", label: "SPB", icon: FileText, children: spbChildren },
  { type: "group", label: "Master Data", icon: Database, children: masterChildren },
  { type: "group", label: "Laporan", icon: ChartBar, children: laporanChildren },
  { type: "group", label: "Setelan", icon: Gear, children: adminKantorSettingsChildren },
  { to: "/sheets", label: "Upload Realisasi", icon: FileXls },
  { to: "/mismatch-corrections", label: "Koreksi Mismatch", icon: Warning, badgeKey: "mismatch_pending" },
  { to: "/audit", label: "Audit Trail", icon: ChartBar },
];

// Petugas Lapangan: ONLY Cek Validasi (they scan/lookup SPB then input jumlah termuat).
const petugasNav = [
  { to: "/loading", label: "Cek Validasi", icon: QrCode },
];

function NavLeaf({ item, active, testIdPrefix, onNavigate, badge }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      data-testid={`${testIdPrefix}${item.to.replace(/\//g, "-")}`}
      className={`flex items-center gap-3 px-5 py-3 border-l-2 text-sm ${
        active
          ? "border-primary bg-secondary text-foreground font-semibold"
          : "border-transparent text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
      }`}
    >
      <Icon size={18} weight={active ? "fill" : "regular"} />
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span
          className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center"
          data-testid={`nav-badge-${item.to.replace(/\//g, "-")}`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

function NavGroup({ item, pathname, onNavigate }) {
  const anyActive = item.children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = React.useState(anyActive);
  const Icon = item.icon;
  const testId = `nav-group-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className={`w-full flex items-center gap-3 px-5 py-3 border-l-2 text-sm text-left ${
          anyActive
            ? "border-primary bg-secondary/60 text-foreground font-semibold"
            : "border-transparent text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
        }`}
      >
        <Icon size={18} weight={anyActive ? "fill" : "regular"} />
        <span className="flex-1">{item.label}</span>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && (
        <div className="bg-secondary/20" data-testid={`${testId}-panel`}>
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            const active = pathname === child.to || pathname.startsWith(child.to + "/");
            return (
              <Link
                key={child.to}
                to={child.to}
                onClick={onNavigate}
                data-testid={`nav-child${child.to.replace(/\//g, "-")}`}
                className={`flex items-center gap-3 pl-12 pr-5 py-2.5 text-sm ${
                  active
                    ? "text-foreground font-semibold bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"
                }`}
              >
                <ChildIcon size={14} weight={active ? "fill" : "regular"} />
                <span>{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const nav = user?.role === "admin"
    ? adminNav
    : user?.role === "admin_kantor"
    ? adminKantorNav
    : petugasNav;

  const showBell = user?.role === "admin" || user?.role === "admin_kantor";

  // Poll pending mismatch-correction count for sidebar badge (admins only).
  const [badges, setBadges] = React.useState({});
  React.useEffect(() => {
    if (!showBell) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const r = await api.get("/mismatch/corrections/count-pending");
        if (!cancelled) setBadges((b) => ({ ...b, mismatch_pending: r.data?.pending || 0 }));
      } catch {
        // Silent — badge is optional UX
      }
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [showBell]);

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const renderNav = (mobile = false) =>
    nav.map((item) => {
      if (item.type === "group") {
        return (
          <NavGroup
            key={item.label}
            item={item}
            pathname={location.pathname}
            onNavigate={mobile ? () => setMobileOpen(false) : undefined}
          />
        );
      }
      const active =
        location.pathname === item.to ||
        (item.to !== "/" && location.pathname.startsWith(item.to));
      return (
        <NavLeaf
          key={item.to}
          item={item}
          active={active}
          testIdPrefix={mobile ? "mobile-nav-" : "nav-"}
          onNavigate={mobile ? () => setMobileOpen(false) : undefined}
          badge={item.badgeKey ? badges[item.badgeKey] : 0}
        />
      );
    });

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar — sticky at top, full viewport height so it never
          leaves view when the main content scrolls. */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-card sticky top-0 h-screen self-start">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
              data-testid="logo-home-link"
            >
              <div className="w-9 h-9 bg-white text-primary flex items-center justify-center flex-shrink-0 border border-primary/30 overflow-hidden">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cement-load-tracker-1/artifacts/2dwgn15g_LOGO.png"
                  alt="Logo Surbakti"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <div className="font-black text-lg leading-none tracking-tight">epool</div>
                <div className="overline mt-1">PT. Sari Bakti Jaya</div>
              </div>
            </Link>
            {showBell && <NotificationBell />}
          </div>
        </div>
        <nav className="py-4 overflow-y-auto flex-1">{renderNav(false)}</nav>
        <div className="p-4 border-t border-border">
          <div className="overline mb-1">Login sebagai</div>
          <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground mb-3">{user?.email}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-none"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <SignOut size={14} className="mr-2" />
            Keluar
          </Button>
          <a
            href="/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block text-[10px] font-mono text-muted-foreground hover:text-foreground text-center tracking-widest uppercase"
            title="Lihat catatan rilis"
            data-testid="app-version-label"
          >
            epool · {APP_VERSION_LABEL}
          </a>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center px-4 justify-between">
        <Link
          to="/"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          data-testid="mobile-logo-home-link"
        >
          <div className="w-8 h-8 bg-white flex items-center justify-center border border-primary/30 overflow-hidden">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cement-load-tracker-1/artifacts/2dwgn15g_LOGO.png"
              alt="Logo Surbakti"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="font-black tracking-tight">SPB</span>
        </Link>
        <div className="flex items-center gap-1">
          {showBell && <NotificationBell />}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 -mr-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed top-14 inset-x-0 bottom-0 z-30 bg-card border-b border-border overflow-y-auto">
          <nav className="py-2">{renderNav(true)}
            <button
              onClick={onLogout}
              className="flex items-center gap-3 px-5 py-4 border-b border-border w-full text-left"
              data-testid="mobile-logout-button"
            >
              <SignOut size={20} />
              Keluar ({user?.email})
            </button>
            <a
              href="/CHANGELOG.md"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-5 py-3 text-[10px] font-mono text-muted-foreground text-center tracking-widest uppercase"
              data-testid="mobile-app-version-label"
            >
              epool · {APP_VERSION_LABEL}
            </a>
          </nav>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 md:pl-0 pt-14 md:pt-0 overflow-x-hidden">
        <div className="max-w-full">{children}</div>
      </main>

      {/* Global floating action button — visible on every authenticated page */}
      <QuickActionsFAB />
    </div>
  );
}
